import { DateTime } from "luxon";
import { Module } from "vuex";

import { EncountersAPI } from "@/api/encounters";
import { VideoRoomsApi } from "@/api/videoRooms";
import { Encounter } from "@/store/modules/encounters/interfaces/Encounter";
import { RoomSettings } from "@/store/modules/visitNote/interfaces/RoomSettings";
import { RoomStatus } from "@/store/modules/visitNote/interfaces/RoomStatus";
import { RootState } from "@/types";
import { RolesEnum } from "@/types/Roles.enum";

interface VideoRoomState {
  encounter: Encounter | null;
  room: RoomStatus | null;
  settings: RoomSettings | null;
  accessToken: string | null;
  isVideo: boolean;
  isAudio: boolean;
  isFullScreen: boolean;
  isPermissionsAccepted: boolean;
  userIsReady: boolean;
}

export const videoRoom: Module<VideoRoomState, RootState> = {
  namespaced: true,
  state: {
    room: null,
    encounter: null,
    settings: null,
    accessToken: null,
    isVideo: true,
    isAudio: true,
    isFullScreen: false,
    isPermissionsAccepted: false,
    userIsReady: false,
  },
  mutations: {
    setEncounter(state, payload: Encounter) {
      state.encounter = payload;
    },
    setRoom(state, payload: RoomStatus) {
      state.room = payload;
    },
    setUserIsReady(state, payload: boolean) {
      state.userIsReady = payload;
    },
    setRoomAccessToken(state, payload: string) {
      state.accessToken = payload;
    },
    setRoomSettings(state, payload: RoomSettings) {
      state.settings = payload;
    },
    setIsVideo(state, payload: boolean) {
      state.isVideo = payload;
    },
    setIsAudio(state, payload: boolean) {
      state.isAudio = payload;
    },
    setIsPermissionsAccepted(state, payload: boolean) {
      state.isPermissionsAccepted = payload;
    },
    setIsFullScreen(state, payload: boolean) {
      state.isFullScreen = payload;
    },
  },
  actions: {
    async startRoom({ commit, state, dispatch }) {
      if (state.room) {
        const room = await EncountersAPI.start(state.room.id);
        commit("setRoom", room);
        await dispatch("getRoomAccessToken");
        return room;
      }
      return null;
    },
    async finishRoom({ commit, state }) {
      if (state.room) {
        const room = await EncountersAPI.finish(state.room.id);
        commit("setRoom", room);
        return room;
      }
      return null;
    },
    async getRoomAccessToken({ commit, state }) {
      if (!state.room) {
        return null;
      }
      const accessToken = await VideoRoomsApi.access(state.room.id);
      commit("setRoomAccessToken", accessToken);
      return accessToken;
    },
    async getEncounter({ commit, dispatch, state }, id: string) {
      if (state.encounter && state.encounter.id === id) {
        return state.encounter;
      }
      const encounter = await EncountersAPI.getById(id);
      commit("setEncounter", encounter);
      await dispatch("getRoom", id);
      return encounter;
    },
    async getRoom({ commit, dispatch, state }, id: string) {
      if (state.room && state.room.id === id) {
        return state.room;
      }
      const room = await VideoRoomsApi.getById(id);
      commit("setRoom", room);
      return room;
    },
    async getRoomStatus({ commit, state }) {
      if (state.room && state.room.id) {
        const room = await VideoRoomsApi.getById(state.room.id);
        commit("setRoom", room);
        return room;
      }
    },
    async setDefaultRoomSettings({ commit, state }) {
      if (state.settings) {
        return state.settings;
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputDevices = devices.filter((device) => device.kind === "videoinput");
      const audioInputDevices = devices.filter((device) => device.kind === "audioinput");
      const audioOutputDevices = devices.filter((device) => device.kind === "audiooutput");
      if (!state.settings) {
        commit("setRoomSettings", {
          videoInput: videoInputDevices[0] && videoInputDevices[0].deviceId,
          audioInput: audioInputDevices[0] && audioInputDevices[0].deviceId,
          audioOutput: audioOutputDevices[0] && audioOutputDevices[0].deviceId,
        });
      }
    },
    async clear({ commit }) {
      commit("setEncounter", null);
      commit("setRoom", null);
      commit("setUserIsReady", false);
      commit("setRoomAccessToken", null);
    },
    async calculateTimer({ state }) {
      if (!state.room || !state.room.startedDate) {
        return;
      }
      const diff = DateTime.fromISO(state.room.startedDate).diffNow("seconds").negate();
      if (diff.as("hours") >= 1) {
        return diff.toFormat("hh:mm:ss");
      }
      return diff.toFormat("mm:ss");
    },
  },
  getters: {
    encounter: (state) => state.encounter,
    room: (state) => state.room,
    settings: (state) => state.settings,
    accessToken: (state) => state.accessToken,
    isVideo: (state) => state.isVideo,
    isAudio: (state) => state.isAudio,
    isFullScreen: (state) => state.isFullScreen,
    isPermissionsAccepted: (state) => state.isPermissionsAccepted,
    userIsReady: (state) => state.userIsReady,
    roomIsReady(state, getters, rootState, rootGetters) {
      if (rootGetters.role === RolesEnum.Practitioner && state.room && state.room.startedDate) {
        return true;
      }
      if (
        [RolesEnum.Patient, RolesEnum.EncounterParticipant].includes(rootGetters.role) &&
        state.room &&
        state.room.startedDate
      ) {
        return state.userIsReady;
      }
      return false;
    },
    roomIsStarted(state) {
      return state.room && state.room.startedDate;
    },
    roomIsFinished(state) {
      return state.room && state.room.finishedDate;
    },
  },
};
